import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  Lightbulb,
  Settings,
  Building2,
  Users,
  RefreshCw,
  Menu,
  X,
  Link,
  Tags,
  Grid3X3,
  Filter,
  MousePointer2,
  Trophy,
  Route,
  Target,
  HelpCircle,
  Bell,
  FileText,
  TrendingUp,
  PieChart,
  Zap,
  Award,
  Workflow,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'ダッシュボード' },
  { to: '/analysis', icon: BarChart3, label: 'データ分析' },
  { to: '/cross-analysis', icon: Grid3X3, label: 'クロス分析' },
  { to: '/funnel-analysis', icon: Filter, label: 'ファネル分析' },
  { to: '/url-click-analysis', icon: MousePointer2, label: 'URLクリック分析' },
  { to: '/scoring', icon: Trophy, label: '行動スコア' },
  { to: '/inflow-analysis', icon: Route, label: '流入経路分析' },
  { to: '/conversion', icon: Target, label: 'コンバージョン' },
  { to: '/ai-analysis', icon: Lightbulb, label: 'AI分析' },
  { to: '/notifications', icon: Bell, label: '通知' },
  { to: '/reports', icon: FileText, label: 'レポート' },
  { to: '/prediction', icon: TrendingUp, label: '予測分析' },
  { to: '/segment-analysis', icon: PieChart, label: 'セグメント分析' },
  { to: '/insights', icon: Zap, label: 'AIインサイト' },
  { to: '/success-cases', icon: Award, label: '成功事例' },
  { to: '/help', icon: HelpCircle, label: '使い方ガイド' },
];

const adminItems = [
  { to: '/admin/companies', icon: Building2, label: '法人管理' },
  { to: '/admin/users', icon: Users, label: 'ユーザー管理' },
  { to: '/admin/scraping', icon: RefreshCw, label: 'スクレイピング' },
  { to: '/admin/automation', icon: Workflow, label: '自動化' },
  { to: '/labels', icon: Tags, label: 'ラベル管理' },
];

export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);

  const handleNavClick = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* ハンバーガーメニューボタン */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        aria-label="メニューを開く"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* オーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* スライドメニュー */}
      <div
        className={cn(
          'fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-300 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <span className="font-bold text-lg text-primary">MAJIRAKU</span>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            aria-label="メニューを閉じる"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-80px)]">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                )
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* 法人管理者用メニュー */}
          {!isSuperAdmin() && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  法人設定
                </span>
              </div>
              <NavLink
                to="/my-company"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  cn(
                    'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  )
                }
              >
                <Link className="w-5 h-5" />
                <span>UTAGE連携</span>
              </NavLink>
              <NavLink
                to="/labels"
                onClick={handleNavClick}
                className={({ isActive }) =>
                  cn(
                    'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  )
                }
              >
                <Tags className="w-5 h-5" />
                <span>ラベル管理</span>
              </NavLink>
            </>
          )}

          {/* スーパー管理者用メニュー */}
          {isSuperAdmin() && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  管理者メニュー
                </span>
              </div>
              {adminItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    )
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {/* アカウント設定 */}
          <div className="pt-4 pb-2">
            <span className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              アカウント
            </span>
          </div>
          <NavLink
            to="/settings"
            onClick={handleNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              )
            }
          >
            <Settings className="w-5 h-5" />
            <span>アカウント設定</span>
          </NavLink>
        </nav>
      </div>
    </>
  );
}
